import React, { useState, useRef, useEffect } from "react";
import { sendAgentMessage } from "../lib/api";
import { renderMarkdown } from "../lib/markdown";

export default function FloatingAgent({ docId }) {
  const [open, setOpen]         = useState(false);
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Hi! Ask me anything about this document or your QA results." }
  ]);

  // Reset chat when navigating to a different document
  const prevDocId = React.useRef(docId);
  useEffect(() => {
    if (docId !== prevDocId.current) {
      prevDocId.current = docId;
      setMessages([{ role: "assistant", content: docId
        ? "Hi! Ask me anything about this document or your QA results."
        : "Hi! Ask me anything about your documents or QA results." }]);
    }
  }, [docId]);

  const [input, setInput]   = useState("");
  const [loading, setLoading] = useState(false);
  const bottomRef           = useRef(null);
  const inputRef            = useRef(null);

  // ── Drag state ───────────────────────────────────────────────
  const [pos, setPos] = useState(() => {
    try {
      const saved = localStorage.getItem("neurogen-qa-floating-agent-pos");
      if (saved) {
        const p = JSON.parse(saved);
        if (typeof p?.right === "number" && typeof p?.bottom === "number") return p;
      }
    } catch (e) {}
    return { right: 24, bottom: 24 };
  });
  const [dragging, setDragging] = useState(false);
  const dragStateRef = useRef(null);

  // Persist position
  useEffect(() => {
    try { localStorage.setItem("neurogen-qa-floating-agent-pos", JSON.stringify(pos)); } catch (e) {}
  }, [pos]);

  // Clamp on window resize so the bubble can't end up off-screen
  useEffect(() => {
    const onResize = () => {
      setPos(p => ({
        right:  Math.max(8, Math.min(window.innerWidth  - 60, p.right)),
        bottom: Math.max(8, Math.min(window.innerHeight - 60, p.bottom)),
      }));
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const handlePointerDown = (e) => {
    if (e.button !== undefined && e.button !== 0) return;
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch (_) {}
    dragStateRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      startRight: pos.right,
      startBottom: pos.bottom,
      moved: false,
      pointerId: e.pointerId,
    };
  };

  const handlePointerMove = (e) => {
    const drag = dragStateRef.current;
    if (!drag || drag.pointerId !== e.pointerId) return;
    const dx = e.clientX - drag.startX;
    const dy = e.clientY - drag.startY;
    if (!drag.moved && Math.hypot(dx, dy) > 5) {
      drag.moved = true;
      setDragging(true);
    }
    if (drag.moved) {
      setPos({
        right:  Math.max(8, Math.min(window.innerWidth  - 60, drag.startRight  - dx)),
        bottom: Math.max(8, Math.min(window.innerHeight - 60, drag.startBottom - dy)),
      });
    }
  };

  const handlePointerUp = (e) => {
    const drag = dragStateRef.current;
    if (!drag || drag.pointerId !== e.pointerId) return;
    try { e.currentTarget.releasePointerCapture(e.pointerId); } catch (_) {}
    const wasDrag = drag.moved;
    dragStateRef.current = null;
    setDragging(false);
    if (!wasDrag) setOpen(o => !o);
  };

  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 100);
      bottomRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [open, messages]);

  const send = async () => {
    const text = input.trim();
    if (!text || loading) return;
    const userMsg = { role: "user", content: text };
    setMessages(prev => [...prev, userMsg]);
    setInput("");
    setLoading(true);
    try {
      const history = [...messages, userMsg].map(m => ({ role: m.role, content: m.content }));
      const contextMsg = docId ? `The user is currently reviewing document: ${docId}. ` : "";
      history[history.length - 1].content = contextMsg + history[history.length - 1].content;
      const res = await sendAgentMessage(history[history.length - 1].content, docId, history.slice(0, -1));
      setMessages(prev => [...prev, { role: "assistant", content: res.response || res.message || "No response." }]);
    } catch (e) {
      setMessages(prev => [...prev, { role: "assistant", content: "Sorry, I couldn't reach the AI service. Please try again." }]);
    }
    setLoading(false);
  };

  const handleKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
  };

  return (
    <div style={{ position:"fixed", bottom: pos.bottom, right: pos.right, zIndex:900, display:"flex", flexDirection:"column", alignItems:"flex-end", gap:10 }}>

      {/* Chat window */}
      {open && (
        <div style={{
          width:360, height:500, background:"var(--white)", borderRadius:14,
          border:"1px solid var(--border)", boxShadow:"0 8px 32px rgba(0,0,0,0.15)",
          display:"flex", flexDirection:"column", overflow:"hidden"
        }}>

          {/* Header */}
          <div style={{
            padding:"12px 16px", borderBottom:"1px solid var(--border)",
            display:"flex", alignItems:"center", justifyContent:"space-between",
            background:"var(--blue-800)", flexShrink:0
          }}>
            <div style={{ display:"flex", alignItems:"center", gap:8 }}>
              <div style={{
                width:28, height:28, borderRadius:"50%", background:"rgba(255,255,255,0.15)",
                display:"flex", alignItems:"center", justifyContent:"center"
              }}>
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="white" strokeWidth="1.5">
                  <path d="M14 8c0 3.314-2.686 6-6 6a5.99 5.99 0 01-3-0.8L1 14l0.8-4A6 6 0 1114 8z"/>
                  <circle cx="5.5" cy="8" r="0.75" fill="white" stroke="none"/>
                  <circle cx="8" cy="8" r="0.75" fill="white" stroke="none"/>
                  <circle cx="10.5" cy="8" r="0.75" fill="white" stroke="none"/>
                </svg>
              </div>
              <div>
                <div style={{ fontSize:12, fontWeight:600, color:"white" }}>AI Assistant</div>
                {docId && (
                  <div style={{ fontSize:10, color:"rgba(255,255,255,0.6)", fontFamily:"var(--font-mono)" }}>
                    {docId.length > 28 ? docId.slice(0, 28) + "…" : docId}
                  </div>
                )}
              </div>
            </div>
            <div style={{ display:"flex", gap:6, alignItems:"center" }}>
              <button
                onClick={() => setMessages([{ role:"assistant", content: docId
                  ? "Hi! Ask me anything about this document or your QA results."
                  : "Hi! Ask me anything about your documents or QA results." }])}
                style={{ background:"rgba(255,255,255,0.12)", border:"none", color:"rgba(255,255,255,0.8)", cursor:"pointer", borderRadius:4, padding:"2px 8px", fontSize:10, fontFamily:"var(--font-mono)" }}
                title="Clear chat"
              >clear</button>
              <button
                onClick={() => setOpen(false)}
                style={{ background:"none", border:"none", cursor:"pointer", color:"rgba(255,255,255,0.7)", padding:4, borderRadius:4 }}
              >
                <svg width="14" height="14" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="3" y1="3" x2="9" y2="9"/><line x1="9" y1="3" x2="3" y2="9"/>
                </svg>
              </button>
            </div>
          </div>

          {/* Messages */}
          <div style={{ flex:1, overflowY:"auto", padding:"12px 14px", display:"flex", flexDirection:"column", gap:10 }}>
            {messages.map((m, i) => (
              <div key={i} style={{ display:"flex", justifyContent: m.role === "user" ? "flex-end" : "flex-start" }}>
                <div style={{
                  maxWidth:"88%",
                  padding:"9px 12px",
                  borderRadius: m.role === "user" ? "12px 12px 3px 12px" : "3px 12px 12px 12px",
                  background: m.role === "user" ? "var(--blue-800)" : "var(--gray-50)",
                  color: m.role === "user" ? "white" : "var(--text-primary)",
                  fontSize:12.5,
                  border: m.role === "assistant" ? "1px solid var(--border)" : "none",
                  wordBreak:"break-word", overflowWrap:"anywhere",
                  lineHeight:1.55
                }}>
                  {m.role === "assistant" ? renderMarkdown(m.content) : m.content}
                </div>
              </div>
            ))}

            {/* Loading dots */}
            {loading && (
              <div style={{ display:"flex", justifyContent:"flex-start" }}>
                <div style={{
                  padding:"10px 14px", borderRadius:"3px 12px 12px 12px",
                  background:"var(--gray-50)", border:"1px solid var(--border)",
                  display:"flex", gap:4, alignItems:"center"
                }}>
                  {[0,1,2].map(d => (
                    <div key={d} style={{
                      width:5, height:5, borderRadius:"50%", background:"var(--text-tertiary)",
                      animation:"bounce 1s infinite", animationDelay:`${d * 0.2}s`
                    }}/>
                  ))}
                </div>
              </div>
            )}
            <div ref={bottomRef}/>
          </div>

          {/* Input */}
          <div style={{ padding:"10px 12px", borderTop:"1px solid var(--border)", display:"flex", gap:8, alignItems:"flex-end", flexShrink:0 }}>
            <textarea
              ref={inputRef}
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKey}
              placeholder="Ask about this document..."
              disabled={loading}
              rows={1}
              style={{
                flex:1, fontSize:12, resize:"none", border:"1px solid var(--border)",
                borderRadius:8, padding:"8px 10px", fontFamily:"var(--font-sans)",
                lineHeight:1.4, maxHeight:80, overflowY:"auto",
                background:"var(--gray-50)", outline:"none"
              }}
            />
            <button
              onClick={send}
              disabled={!input.trim() || loading}
              style={{
                width:32, height:32, borderRadius:8, border:"none", cursor:"pointer",
                background: input.trim() && !loading ? "var(--blue-800)" : "var(--gray-200)",
                color: input.trim() && !loading ? "white" : "var(--text-tertiary)",
                display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0,
                transition:"all 0.15s"
              }}
            >
              <svg width="13" height="13" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="7" y1="12" x2="7" y2="2"/><polyline points="3 6 7 2 11 6" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Toggle button — draggable, click to open */}
      <button
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
        style={{
          width:52, height:52, borderRadius:"50%", border:"none",
          background:"var(--blue-800)", color:"white",
          boxShadow: dragging ? "0 8px 24px rgba(30,58,138,0.5)" : "0 4px 16px rgba(30,58,138,0.35)",
          display:"flex", alignItems:"center", justifyContent:"center",
          transition: dragging ? "none" : "transform 0.2s, box-shadow 0.2s",
          transform: dragging ? "scale(1.05)" : "scale(1)",
          cursor: dragging ? "grabbing" : "grab",
          touchAction: "none",
          userSelect: "none",
        }}
        title="AI Assistant — drag to move, click to open"
      >
        {open ? (
          <svg width="18" height="18" viewBox="0 0 12 12" fill="none" stroke="white" strokeWidth="2.5">
            <line x1="3" y1="3" x2="9" y2="9"/><line x1="9" y1="3" x2="3" y2="9"/>
          </svg>
        ) : (
          <svg width="20" height="20" viewBox="0 0 16 16" fill="none" stroke="white" strokeWidth="1.5">
            <path d="M14 8c0 3.314-2.686 6-6 6a5.99 5.99 0 01-3-0.8L1 14l0.8-4A6 6 0 1114 8z"/>
            <circle cx="5.5" cy="8" r="0.75" fill="white" stroke="none"/>
            <circle cx="8" cy="8" r="0.75" fill="white" stroke="none"/>
            <circle cx="10.5" cy="8" r="0.75" fill="white" stroke="none"/>
          </svg>
        )}
      </button>

      <style>{`
        @keyframes bounce {
          0%, 80%, 100% { transform: translateY(0); opacity: 0.5; }
          40% { transform: translateY(-5px); opacity: 1; }
        }
      `}</style>
    </div>
  );
}